import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

import { META_EXPRESSION_NODES_CHANGED } from "@/components/tiptap/tiptap-expression/expressionNodesChanged";
import { insertNode } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { isGroupClosingSymbol, isGroupOpeningSymbol } from "@/models/nodes/groups/utils";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import type { InlineNodeAttrs } from "@corca/tiptap";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";

type WrapInGroupOptions = {
  doc: DocumentModel;
};

type WrapInGroupPluginState = {
  /**
   * Regions to scan for a group symbol beside the cursored expression.
   */
  ranges: Array<{
    minPos: number;
    maxPos: number;
  }>;
};

const EMPTY_STATE: WrapInGroupPluginState = { ranges: [] };

const wrapInGroupPluginKey = new PluginKey<WrapInGroupPluginState>("wrapInGroup");

export const WrapInGroup = Extension.create<WrapInGroupOptions>({
  name: "wrapInGroup",

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        key: wrapInGroupPluginKey,
        state: {
          init(): WrapInGroupPluginState {
            return EMPTY_STATE;
          },
          apply(tr, value): WrapInGroupPluginState {
            let minPos = Infinity;
            let maxPos = -Infinity;

            const { isChangeOrigin, isUndoRedoOperation } =
              (tr.getMeta(ySyncPluginKey) as Record<string, boolean>) || {};

            if (isUndoRedoOperation || isChangeOrigin) {
              return EMPTY_STATE;
            }

            const expressionPositions = tr.getMeta(META_EXPRESSION_NODES_CHANGED) as
              | number[]
              | undefined;
            if (expressionPositions?.length && doc.editor.editorState.isExpression) {
              return {
                ranges: expressionPositions.map((pos) => ({ minPos: pos, maxPos: pos + 1 })),
              };
            }

            if (!tr.docChanged || !doc.editor.editorState.isExpression) {
              return value;
            }

            tr.mapping.maps.forEach((map, i) => {
              const step = tr.steps[i];

              if (step && "from" in step && "to" in step) {
                const to = step.to as number;
                const from = step.from as number;

                // Ignore full document replacements
                if (from === 0 && to === tr.doc.nodeSize - 2) {
                  return;
                }
              }

              map.forEach((_, __, newStart, newEnd) => {
                minPos = Math.min(minPos, newStart);
                maxPos = Math.max(maxPos, newEnd);
              });
            });

            return {
              ranges: [
                {
                  minPos,
                  maxPos: Math.max(0, Math.min(maxPos, tr.doc.nodeSize - 1)),
                },
              ],
            };
          },
        },
        view() {
          return {
            update(view) {
              const { state } = view;
              const pluginState = wrapInGroupPluginKey.getState(state);
              const ranges = pluginState?.ranges;

              if (!ranges?.length) {
                return;
              }

              const { tr } = view.state;
              const { cursor } = doc;

              if (!cursorIsActive(cursor)) {
                return;
              }

              const handleNode = (pmNode: PMNode, pos: number) => {
                if (pmNode && pmNode.type.name === "inlineExpression") {
                  const { nodeId } = pmNode.attrs as InlineNodeAttrs;
                  const node = doc.nodes.get(nodeId) as WrapperNodeModel | undefined;

                  if (!node || cursor.node !== node.inlineNode) {
                    return;
                  }

                  const positionBefore = pos - 1;
                  const positionAfter = pos + 1;

                  const openingSymbol = getOpeningSymbol(state, positionBefore);
                  const closingSymbol = getClosingSymbol(state, positionAfter);

                  if (!openingSymbol && !closingSymbol) {
                    return;
                  }

                  const { nodes = [] } = node.inlineNode ?? {};
                  const [firstNode] = nodes;
                  const isSingleNumber = nodes.length === 1 && firstNode.is(NumberNodeModel);

                  if (isSingleNumber) {
                    if (!doc.editor.forceWrapInGroup) {
                      return;
                    }

                    doc.editor.forceWrapInGroup = false;
                  }

                  if (firstNode.is(GroupNodeModel)) {
                    if (firstNode.openingSymbol && firstNode.closingSymbol) {
                      return;
                    }

                    doc.transact(
                      () => {
                        firstNode.openingSymbol = openingSymbol || firstNode.openingSymbol;
                        firstNode.closingSymbol = closingSymbol || firstNode.closingSymbol;
                      },
                      "applyGroupSymbols",
                      { preventNewUndoStack: true },
                    );
                  } else {
                    doc.transact(
                      () => {
                        const cursorIndex = doc.cursor.index;
                        const groupNode = doc.create(
                          GroupNodeModel,
                          [],
                          openingSymbol,
                          closingSymbol,
                        );

                        insertNode(cursor, groupNode);
                        groupNode.inlineNode?.appendChildren(nodes);
                        cursor.moveTo(groupNode?.inlineNode, cursorIndex);
                      },
                      "wrapInGroup",
                      { preventNewUndoStack: true },
                    );
                  }

                  if (closingSymbol) {
                    tr.deleteRange(positionAfter, positionAfter + 1);
                  }

                  if (openingSymbol) {
                    tr.deleteRange(positionBefore, positionBefore + 1);
                  }
                }
              };

              const descendingRanges = [...ranges].sort((a, b) => b.minPos - a.minPos);

              for (const { minPos, maxPos } of descendingRanges) {
                if (minPos <= maxPos && maxPos < state.doc.nodeSize - 1) {
                  view.state.doc.nodesBetween(minPos, maxPos, handleNode);
                }
              }

              if (tr.steps.length > 0) {
                view.dispatch(tr);
              }
            },
          };
        },
      }),
    ];
  },
});

function getOpeningSymbol(state: EditorState, position: number) {
  const nodeBefore = position > 0 ? state.doc.nodeAt(position) : null;
  const lastSymbolBefore = nodeBefore?.text?.slice(-1);

  if (lastSymbolBefore) {
    return isGroupOpeningSymbol(lastSymbolBefore) ? lastSymbolBefore : undefined;
  }

  return undefined;
}

function getClosingSymbol(state: EditorState, position: number) {
  const nodeAfter = position < state.doc.nodeSize ? state.doc.nodeAt(position) : null;
  const firstSymbolAfter = nodeAfter?.text?.[0];

  if (firstSymbolAfter) {
    return isGroupClosingSymbol(firstSymbolAfter) ? firstSymbolAfter : undefined;
  }

  return undefined;
}
